import {
  CreateEpisodePayload,
  CreateEpisodeFollowUpPayload,
  FollowUpStatus,
  FollowUpTaskType,
  ReasonCode,
} from "../types/incapacity";

export type FollowUpFormValues = {
  /** Set for follow-ups that already exist on the server; absent for new rows. */
  id?: string;
  taskType: FollowUpTaskType | "";
  dueAt: string;
  /** Phased out: shown read-only on older follow-ups, never sent. */
  description?: string;
  /** Tracked for existing follow-ups so status can be edited in place. */
  status?: FollowUpStatus;
};

export type CreateEpisodeFormValues = {
  reportedAt: string;
  startDate: string;
  estimatedReturnDate: string;
  returnDate: string;
  reasonCode: ReasonCode | "";
  verified: boolean;
  musculoskeletal: boolean;
  comment: string;
  followUps: FollowUpFormValues[];
};

export const reasonCodeOptions: ReasonCode[] = [
  "illness",
  "work_accident",
  "off_the_job_accident",
  "doctor_visit",
  "sick_child",
];

export const formatReasonCode = (reasonCode: ReasonCode | string) => {
  switch (reasonCode) {
    case "illness":
      return "Illness";
    case "work_accident":
      return "Work accident";
    case "off_the_job_accident":
      return "Off the job accident";
    case "doctor_visit":
      return "Visiting a doctor";
    case "sick_child":
      return "Sick child";
    default:
      return reasonCode;
  }
};

/** Types offered for new follow-ups; check in and request document are retired. */
export const followUpTaskTypeOptions: FollowUpTaskType[] = [
  "call_employee",
  "check_up",
];

/** Options for a follow-up's type select, keeping a retired type it already has. */
export const getFollowUpTaskTypeOptions = (
  current: FollowUpTaskType | "",
): FollowUpTaskType[] =>
  current && !followUpTaskTypeOptions.includes(current)
    ? [...followUpTaskTypeOptions, current]
    : followUpTaskTypeOptions;

export const toIsoFromLocalDateTime = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

export const formatFollowUpType = (taskType: FollowUpTaskType): string => {
  switch (taskType) {
    case "call_employee":
      return "Call employee";
    case "check_in":
      return "Check in";
    case "request_document":
      return "Request document";
    case "check_up":
      return "Check-up";
    default:
      return taskType;
  }
};

export const followUpStatusOptions: FollowUpStatus[] = [
  "pending",
  "in_progress",
  "completed",
  "cancelled",
];

export const formatFollowUpStatus = (status: FollowUpStatus): string => {
  switch (status) {
    case "pending":
      return "Pending";
    case "in_progress":
      return "In progress";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
};

export const toDateTimeLocalValue = (date = new Date()) => {
  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60_000,
  );
  return localDate.toISOString().slice(0, 16);
};

export const toCreateEpisodePayload = (
  values: CreateEpisodeFormValues,
  employeeId: string,
): CreateEpisodePayload => {
  if (!values.reasonCode) {
    throw new Error("Reason code is required");
  }

  return {
    employeeId,
    reportedAt: toIsoFromLocalDateTime(values.reportedAt),
    startDate: values.startDate,
    estimatedReturnDate: values.estimatedReturnDate,
    returnDate: values.returnDate || undefined,
    reasonCode: values.reasonCode,
    musculoskeletal: values.musculoskeletal,
  };
};

export const toCreateFollowUpPayloads = (
  followUps: FollowUpFormValues[],
): CreateEpisodeFollowUpPayload[] =>
  followUps.reduce<CreateEpisodeFollowUpPayload[]>((payloads, followUp) => {
    if (!followUp.taskType || !followUp.dueAt) {
      return payloads;
    }

    payloads.push({
      type: followUp.taskType,
      dueDate: toIsoFromLocalDateTime(followUp.dueAt),
    });

    return payloads;
  }, []);
